import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Switch } from '@shared/components/ui/switch'
import { Textarea } from '@shared/components/ui/textarea'
import { Separator } from '@shared/components/ui/separator'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faSave,
  faDatabase,
  faShieldAlt,
  faEnvelope,
  faGlobe
} from '@fortawesome/free-solid-svg-icons'
import { toast } from '@shared/hooks/use-toast'
import { apiUrl } from '@/lib/api'
import { PageHeader } from '@shared/components/ui/page-header'

interface SystemSettings {
  platform: { name: string; domain: string; supportEmail: string; maintenanceMode: boolean }
  database: { type: string; connectionString: string; backupEnabled: boolean; backupFrequency: string }
  security: { sessionTimeout: number; maxLoginAttempts: number; requireMFA: boolean; passwordMinLength: number }
  email: { provider: string; apiKey: string; fromAddress: string; templates: { welcome: string; resetPassword: string } }
  rolePresets?: Array<{ name: string; permissionKeys: string[] }>
}

const SystemSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings>({
    platform: { name: 'Motiv', domain: 'motivsuite.com', supportEmail: 'support@motivsuite.com', maintenanceMode: false },
    database: { type: 'PostgreSQL', connectionString: 'postgresql://localhost:5432/motiv', backupEnabled: true, backupFrequency: 'daily' },
    security: { sessionTimeout: 24, maxLoginAttempts: 5, requireMFA: false, passwordMinLength: 8 },
    email: { provider: 'SendGrid', apiKey: '********', fromAddress: 'noreply@motiv.com', templates: { welcome: 'Welcome to Motiv!', resetPassword: 'Reset your password' } },
    rolePresets: []
  })

  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { auth } = await import('@/lib/firebase')
        const idToken = await auth.currentUser?.getIdToken()
        const response = await fetch(apiUrl('/api/admin/settings'), { headers: idToken ? { 'Authorization': `Bearer ${idToken}` } : {} })
        if (!response.ok) throw new Error('Failed to load settings')
        const currentSettings = await response.json()
        setSettings((prev) => ({ ...prev, ...currentSettings }))
      } catch (error) {
        console.error('Error loading settings:', error)
        toast({ title: 'Error', description: 'Failed to load current settings', variant: 'destructive' })
      } finally { setIsLoading(false) }
    }
    loadSettings()
  }, [])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const { auth } = await import('@/lib/firebase')
      const idToken = await auth.currentUser?.getIdToken()
      const response = await fetch(apiUrl('/api/admin/settings'), { method: 'PUT', headers: { 'Content-Type': 'application/json', ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}) }, body: JSON.stringify(settings) })
      if (!response.ok) throw new Error(`Failed to save settings: ${response.status} ${response.statusText}`)
      await response.json()
      toast({ title: 'Success', description: 'Settings saved successfully' })
    } catch (error) {
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Failed to save settings', variant: 'destructive' })
    } finally { setIsSaving(false) }
  }

  const updateSetting = (section: keyof SystemSettings, key: string, value: any) => {
    setSettings(prev => ({ ...prev, [section]: { ...(prev as any)[section], [key]: value } as any }))
  }

  const addPreset = () => {
    setSettings(prev => ({ ...prev, rolePresets: [...(prev.rolePresets||[]), { name: 'New Preset', permissionKeys: [] }] }))
  }
  const removePreset = (idx: number) => {
    setSettings(prev => ({ ...prev, rolePresets: (prev.rolePresets||[]).filter((_, i) => i !== idx) }))
  }
  const updatePresetName = (idx: number, name: string) => {
    setSettings(prev => ({ ...prev, rolePresets: (prev.rolePresets||[]).map((p, i) => i===idx ? { ...p, name } : p) }))
  }
  const updatePresetKeys = (idx: number, keysCsv: string) => {
    const keys = keysCsv.split(',').map(s=>s.trim()).filter(Boolean)
    setSettings(prev => ({ ...prev, rolePresets: (prev.rolePresets||[]).map((p, i) => i===idx ? { ...p, permissionKeys: keys } : p) }))
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="System Settings" description="Configure platform settings and system preferences" />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader title="System Settings" description="Configure platform settings and system preferences">
        <Button onClick={handleSave} disabled={isSaving}><FontAwesomeIcon icon={faSave} className="mr-2 h-4 w-4" />{isSaving ? 'Saving...' : 'Save Settings'}</Button>
      </PageHeader>

      <div className="grid gap-6">
        {/* Platform Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FontAwesomeIcon icon={faGlobe} className="h-5 w-5" />
              Platform Settings
            </CardTitle>
            <CardDescription>
              Basic platform configuration and branding
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="platformName">Platform Name</Label>
                <Input
                  id="platformName"
                  value={settings.platform.name}
                  onChange={(e) => updateSetting('platform', 'name', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="domain">Domain</Label>
                <Input
                  id="domain"
                  value={settings.platform.domain}
                  onChange={(e) => updateSetting('platform', 'domain', e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="supportEmail">Support Email</Label>
              <Input
                id="supportEmail"
                type="email"
                value={settings.platform.supportEmail}
                onChange={(e) => updateSetting('platform', 'supportEmail', e.target.value)}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="maintenanceMode"
                checked={settings.platform.maintenanceMode}
                onCheckedChange={(checked) => updateSetting('platform', 'maintenanceMode', checked)}
              />
              <Label htmlFor="maintenanceMode">Maintenance Mode</Label>
            </div>
          </CardContent>
        </Card>

        {/* Database Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FontAwesomeIcon icon={faDatabase} className="h-5 w-5" />
              Database Settings
            </CardTitle>
            <CardDescription>
              Database configuration and backup settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dbType">Database Type</Label>
                <Input
                  id="dbType"
                  value={settings.database.type}
                  onChange={(e) => updateSetting('database', 'type', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="backupFrequency">Backup Frequency</Label>
                <Select
                  value={settings.database.backupFrequency}
                  onValueChange={(value) => updateSetting('database', 'backupFrequency', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select backup frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="connectionString">Connection String</Label>
              <Input
                id="connectionString"
                type="password"
                value={settings.database.connectionString}
                onChange={(e) => updateSetting('database', 'connectionString', e.target.value)}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="backupEnabled"
                checked={settings.database.backupEnabled}
                onCheckedChange={(checked) => updateSetting('database', 'backupEnabled', checked)}
              />
              <Label htmlFor="backupEnabled">Enable Automated Backups</Label>
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FontAwesomeIcon icon={faShieldAlt} className="h-5 w-5" />
              Security Settings
            </CardTitle>
            <CardDescription>
              Security and authentication configuration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sessionTimeout">Session Timeout (hours)</Label>
                <Input
                  id="sessionTimeout"
                  type="number"
                  value={settings.security.sessionTimeout}
                  onChange={(e) => updateSetting('security', 'sessionTimeout', parseInt(e.target.value))}
                />
              </div>
              <div>
                <Label htmlFor="maxLoginAttempts">Max Login Attempts</Label>
                <Input
                  id="maxLoginAttempts"
                  type="number"
                  value={settings.security.maxLoginAttempts}
                  onChange={(e) => updateSetting('security', 'maxLoginAttempts', parseInt(e.target.value))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="requireMFA"
                  checked={settings.security.requireMFA}
                  onCheckedChange={(checked) => updateSetting('security', 'requireMFA', checked)}
                />
                <Label htmlFor="requireMFA">Require Multi-Factor Authentication</Label>
              </div>
              <div>
                <Label htmlFor="passwordMinLength">Minimum Password Length</Label>
                <Input
                  id="passwordMinLength"
                  type="number"
                  value={settings.security.passwordMinLength}
                  onChange={(e) => updateSetting('security', 'passwordMinLength', parseInt(e.target.value))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FontAwesomeIcon icon={faEnvelope} className="h-5 w-5" />
              Email Settings
            </CardTitle>
            <CardDescription>
              Email provider configuration and templates
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="emailProvider">Email Provider</Label>
                <Select
                  value={settings.email.provider}
                  onValueChange={(value) => updateSetting('email', 'provider', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select email provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SendGrid">SendGrid</SelectItem>
                    <SelectItem value="Mailgun">Mailgun</SelectItem>
                    <SelectItem value="AWS SES">AWS SES</SelectItem>
                    <SelectItem value="SMTP">SMTP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="fromAddress">From Address</Label>
                <Input
                  id="fromAddress"
                  type="email"
                  value={settings.email.fromAddress}
                  onChange={(e) => updateSetting('email', 'fromAddress', e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="apiKey">API Key</Label>
              <Input
                id="apiKey"
                type="password"
                value={settings.email.apiKey}
                onChange={(e) => updateSetting('email', 'apiKey', e.target.value)}
              />
            </div>
            <Separator />
            <div>
              <Label htmlFor="welcomeTemplate">Welcome Email Template</Label>
              <Textarea
                id="welcomeTemplate"
                value={settings.email.templates.welcome}
                onChange={(e) => updateSetting('email', 'templates', {
                  ...settings.email.templates,
                  welcome: e.target.value
                })}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="resetTemplate">Password Reset Template</Label>
              <Textarea
                id="resetTemplate"
                value={settings.email.templates.resetPassword}
                onChange={(e) => updateSetting('email', 'templates', {
                  ...settings.email.templates,
                  resetPassword: e.target.value
                })}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Role Permission Presets */}
        <Card>
          <CardHeader>
            <CardTitle>Role Permission Presets</CardTitle>
            <CardDescription>Define reusable permission sets to apply on roles</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(settings.rolePresets||[]).map((p, idx) => (
              <div key={idx} className="border rounded p-3 space-y-2">
                <div className="grid md:grid-cols-3 gap-2 items-center">
                  <div>
                    <Label>Preset Name</Label>
                    <Input value={p.name} onChange={(e)=>updatePresetName(idx, e.target.value)} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Permission Keys (comma-separated)</Label>
                    <Input value={(p.permissionKeys||[]).join(', ')} onChange={(e)=>updatePresetKeys(idx, e.target.value)} />
                  </div>
                </div>
                <div className="text-right">
                  <Button variant="destructive" size="sm" onClick={()=>removePreset(idx)}>Remove</Button>
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addPreset}>Add Preset</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default SystemSettingsPage
